import type { Assignee, RequestListItem } from "@/api";

type AssignmentRow = Pick<RequestListItem, "id" | "assignees" | "subject" | "from" | "reference">;

/**
 * What was on each request last time we looked, as principal keys (#803).
 *
 * A single id no longer answers "is this mine": several people and whole teams can be on one
 * request at once, so the snapshot has to be the set, and "newly mine" means a key that is mine
 * appeared in it.
 */
export type AssignmentSnapshot = Map<string, ReadonlySet<string>>;

export interface AssignmentNotice {
  id: string;
  subject: string | null;
  from: string | null;
  reference?: string | null;
}

/** `user:<id>` / `team:<id>` — the same shape on both sides of the comparison. */
export function principalKey(principal: Assignee | { kind: string; id: string }): string {
  return `${principal.kind}:${principal.id}`;
}

/**
 * Every key that means "me": my own account, and every team I am currently in. Team membership
 * comes from the session's own memberships, so an operator put on a request through their shift
 * is told about it exactly like one named directly.
 */
export function ownPrincipalKeys(
  operatorId: string | null | undefined,
  teamIds: readonly string[] = [],
): ReadonlySet<string> {
  const keys = new Set<string>();
  if (operatorId) keys.add(`user:${operatorId}`);
  for (const teamId of teamIds) keys.add(`team:${teamId}`);
  return keys;
}

export function buildAssignmentSnapshot(rows: AssignmentRow[]): AssignmentSnapshot {
  return new Map(rows.map((row) => [row.id, new Set(row.assignees.map(principalKey))]));
}

export function newlyAssignedToOperator(
  previous: AssignmentSnapshot,
  rows: AssignmentRow[],
  own: ReadonlySet<string>,
  suppressedIds: ReadonlySet<string> = new Set(),
): AssignmentNotice[] {
  if (!own.size) return [];
  return rows
    .filter((row) => {
      if (suppressedIds.has(row.id)) return false;
      const now = row.assignees.map(principalKey).filter((key) => own.has(key));
      if (!now.length) return false;
      // Only a key that was NOT there before is news. Somebody else joining a request already
      // mine, or a team being renamed, must not re-announce work the operator has seen.
      const before = previous.get(row.id);
      if (!before) return false;
      return now.some((key) => !before.has(key));
    })
    .map((row) => ({
      id: row.id,
      subject: row.subject,
      from: row.from,
      reference: row.reference,
    }));
}

export function activeSuppressionIds(suppressions: Map<string, number>, now = Date.now()): Set<string> {
  const active = new Set<string>();
  for (const [requestId, expiresAt] of suppressions) {
    if (expiresAt > now) {
      active.add(requestId);
    } else {
      suppressions.delete(requestId);
    }
  }
  return active;
}
